import { deploymentRegion, type DeploymentRegion } from 'app/constants';

/**
 * A value that differs between deployment regions.
 *
 * The UK is the baseline every deployment falls back to, so a region only
 * needs an entry when its version actually differs. Adding a region to
 * `DEPLOYMENT_REGIONS` widens this type automatically.
 */
export type RegionVariants<T> = { uk: T } & Partial<Record<DeploymentRegion, T>>;

/**
 * Picks the variant for the region this build is deployed to, so each regional
 * difference is one expression instead of a conditional threaded through the
 * call site:
 *
 * ```ts
 * const label = byRegion({ uk: 'Organisation', canada: 'Organization' });
 * const features = ['Prescriptions', ...byRegion({ uk: ['OTC'], canada: [] })];
 * ```
 *
 * Regions without an entry get the UK baseline. An explicit `undefined`
 * variant counts as "no override"; use `null` to mean "nothing" — that is what
 * makes `RegionVariant` able to render no markup for a region.
 *
 * `region` is only passed explicitly by tests, which would otherwise have to
 * reset modules to move the deployment region.
 */
export function byRegion<T>(variants: RegionVariants<T>, region: string = deploymentRegion): T {
  const variant = (variants as Partial<Record<string, T>>)[region];
  return variant === undefined ? variants.uk : variant;
}
